import { motion } from 'framer-motion';
import { ArrowRight, Play, Shield, Clock, Award, DollarSign } from 'lucide-react';

export const HeroSection = ({ onQuoteClick }) => {
  const features = [
    { icon: Shield, text: 'Fully Insured' },
    { icon: Clock, text: 'Fast Service' },
    { icon: Award, text: '30 Years' },
    { icon: DollarSign, text: 'Free Quotes' },
  ];

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image with Parallax */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1637681241471-de08fe750e94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxmcmVzaCUyMGJsYWNrJTIwYXNwaGFsdCUyMGRyaXZld2F5JTIwdGV4dHVyZXxlbnwwfHx8fDE3NzAxNjU2Nzh8MA&ixlib=rb-4.1.0&q=85')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-neutral-950" />
        
        {/* Red accent stripes */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(220,38,38,0.2) 35px, rgba(220,38,38,0.2) 70px)'
          }} />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-32 lg:py-40">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Badge with logo */}
              <div className="inline-flex items-center gap-3 bg-red-600/20 border border-red-600/50 rounded-sm px-4 py-2 mb-6">
                <img src="/images/logo.jpg" alt="Rhino Seal" className="w-8 h-8 rounded-full" />
                <span className="text-red-400 text-sm font-semibold uppercase tracking-wider">
                  Worcester's #1 Sealcoating Experts
                </span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-none mb-6">
                PROTECT YOUR
                <span className="block text-red-500">PAVEMENT</span>
                <span className="block text-gray-300">INVESTMENT</span>
              </h1>

              <p className="text-xl text-neutral-300 mb-8 max-w-xl leading-relaxed">
                The only place to call for all your pavement maintenance needs. 
                Professional sealcoating and crack filling for driveways and parking lots 
                across Central Massachusetts.
              </p>

              {/* CTA Buttons - RED themed */}
              <div className="flex flex-wrap gap-4 mb-12">
                <motion.button
                  onClick={onQuoteClick}
                  className="bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-wider px-8 py-4 rounded-sm flex items-center gap-2 text-lg shadow-lg"
                  whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(220, 38, 38, 0.5)' }}
                  whileTap={{ scale: 0.95 }}
                  data-testid="hero-quote-btn"
                >
                  Get a Free Quote
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
                
                <motion.a
                  href="#gallery"
                  className="border-2 border-gray-400 text-white hover:bg-gray-400 hover:text-black font-bold uppercase tracking-wider px-8 py-4 rounded-sm flex items-center gap-2 text-lg transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid="hero-gallery-btn"
                >
                  <Play className="w-5 h-5" />
                  See Our Work
                </motion.a>
              </div>

              {/* Feature badges */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.text}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="flex items-center gap-2 text-neutral-300"
                    whileHover={{ scale: 1.1, color: '#ef4444' }}
                  >
                    <feature.icon className="w-5 h-5 text-red-500" />
                    <span className="text-sm font-medium">{feature.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right Column - BIG LOGO & Stats */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block"
          >
            <div className="relative">
              {/* Main card with BIG LOGO */}
              <div className="bg-neutral-900/80 backdrop-blur-sm border-2 border-red-600/30 p-8 rounded-sm">
                {/* Giant Rhino Logo */}
                <motion.div 
                  className="flex justify-center mb-8"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                >
                  <img
                    src="/images/logo.jpg"
                    alt="Rhino Seal"
                    className="w-48 h-48 rounded-full object-cover border-4 border-red-600 shadow-2xl shadow-red-600/30"
                  />
                </motion.div>

                <div className="grid grid-cols-2 gap-6">
                  <motion.div 
                    className="text-center p-6 bg-neutral-800/50 rounded-sm border border-gray-700"
                    whileHover={{ scale: 1.05, borderColor: '#dc2626', boxShadow: '0 0 20px rgba(220, 38, 38, 0.3)' }}
                  >
                    <div className="text-5xl font-black text-red-500 font-mono">30</div>
                    <div className="text-gray-400 text-sm uppercase tracking-wider mt-2">Years Experience</div>
                  </motion.div>
                  <motion.div 
                    className="text-center p-6 bg-neutral-800/50 rounded-sm border border-gray-700"
                    whileHover={{ scale: 1.05, borderColor: '#dc2626', boxShadow: '0 0 20px rgba(220, 38, 38, 0.3)' }}
                  >
                    <div className="text-5xl font-black text-gray-300 font-mono">1K+</div>
                    <div className="text-gray-400 text-sm uppercase tracking-wider mt-2">Happy Customers</div>
                  </motion.div>
                  <motion.div 
                    className="text-center p-6 bg-neutral-800/50 rounded-sm border border-gray-700"
                    whileHover={{ scale: 1.05, borderColor: '#dc2626', boxShadow: '0 0 20px rgba(220, 38, 38, 0.3)' }}
                  >
                    <div className="text-5xl font-black text-green-500 font-mono">100%</div>
                    <div className="text-gray-400 text-sm uppercase tracking-wider mt-2">Satisfaction</div>
                  </motion.div>
                  <motion.div 
                    className="text-center p-6 bg-neutral-800/50 rounded-sm border border-gray-700"
                    whileHover={{ scale: 1.05, borderColor: '#dc2626', boxShadow: '0 0 20px rgba(220, 38, 38, 0.3)' }}
                  >
                    <div className="text-5xl font-black text-white font-mono">5★</div>
                    <div className="text-gray-400 text-sm uppercase tracking-wider mt-2">Rated Service</div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <div className="w-6 h-10 border-2 border-red-500/50 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-red-500 rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
